import { constants, type dto, type infra } from '@photo-vault/shared';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api.js';

// REST implementation of MetadataStore against the Photo Vault HTTP API.
// Every method here is a 1:1 translation of the previous features/*/api.ts
// modules (which now no longer exist as standalone files).
export const awsMetadataStore: infra.MetadataStore = {
  // ---- Identity bootstrap --------------------------------------------------

  loginBootstrap(token) {
    return apiPost<dto.LoginBootstrapResponse>('/auth/login', {}, { token });
  },

  recoveryBootstrap(token) {
    return apiPost<dto.RecoveryBootstrapResponse>('/auth/recovery-bootstrap', {}, { token });
  },

  registerIdentity(body, token) {
    return apiPost<dto.RegisterResponse>('/auth/register', body, { token });
  },

  verifyPassphrase(body, token) {
    return apiPost<dto.VerifyPassphraseResponse>('/auth/verify-passphrase', body, { token });
  },

  changePassphrase(body, token) {
    return apiPost<dto.ChangePassphraseResponse>('/auth/change-passphrase', body, { token });
  },

  // ---- Albums --------------------------------------------------------------

  listAlbums(token) {
    return apiGet<dto.AlbumListResponse>('/albums', { token });
  },

  fetchAlbumTree(rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiGet<dto.AlbumTreeResponse>(`/albums/tree?${qs.toString()}`, { token });
  },

  createRootAlbum(body, token) {
    return apiPost<dto.Album>('/albums', body, { token });
  },

  createChildAlbum(body, token) {
    return apiPost<dto.Album>('/albums', body, { token });
  },

  patchAlbum(albumId, body, token) {
    return apiPatch<dto.Album>(`/albums/${encodeURIComponent(albumId)}`, body, { token });
  },

  deleteAlbum(albumId, token) {
    return apiDelete<{ deletedIds: string[] }>(
      `/albums/${encodeURIComponent(albumId)}`,
      undefined,
      { token },
    );
  },

  // ---- Photos --------------------------------------------------------------

  listAlbumPhotos(albumId, token, rootId) {
    // rootId is mandatory in viewer-mode lookups: the backend keys albums by
    // ownerId, so a viewer querying a child album needs to disambiguate via
    // the root.
    const qs = rootId ? `?rootId=${encodeURIComponent(rootId)}` : '';
    return apiGet<dto.AlbumPhotosResponse>(`/albums/${encodeURIComponent(albumId)}/photos${qs}`, {
      token,
    });
  },

  fetchRootPhotosTree(rootId, token) {
    return apiGet<dto.RootPhotosTreeResponse>(`/albums/${encodeURIComponent(rootId)}/photos-tree`, {
      token,
    });
  },

  listRootPhotos(rootId, token, cursor) {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiGet<dto.RootPhotosPageResponse>(
      `/roots/${encodeURIComponent(rootId)}/photos${suffix}`,
      { token },
    );
  },

  deletePhotosBatch(body, token) {
    return apiPost<dto.DeletePhotosResponse>('/photos/delete-batch', body, { token });
  },

  movePhotosBatch(body, token) {
    return apiPost<dto.MovePhotosResponse>('/photos/move-batch', body, { token });
  },

  // ---- Uploads -------------------------------------------------------------

  uploadsBatch(body, token) {
    return apiPost<dto.UploadBatchResponse>('/uploads/batch', body, { token });
  },

  uploadsComplete(body, token) {
    return apiPost<{ ok: true }>('/uploads/complete', body, { token });
  },

  // ---- Downloads -----------------------------------------------------------

  downloadsBatch(body, token) {
    return apiPost<dto.DownloadBatchResponse>('/downloads/batch', body, { token });
  },

  // Transparently chunks at DOWNLOAD_BATCH_MAX_FILES so callers can pass an
  // entire album's worth of photos without hitting the server-side cap.
  // onChunk fires once per batch as soon as it lands, so the grid can render
  // tiles progressively while later chunks are still being requested.
  async downloadsBatchAllStreaming(items, token, onChunk) {
    const chunkSize = constants.DOWNLOAD_BATCH_MAX_FILES;
    for (let i = 0; i < items.length; i += chunkSize) {
      const { items: chunk } = await this.downloadsBatch(
        { items: items.slice(i, i + chunkSize) },
        token,
      );
      onChunk(chunk);
    }
  },

  // ---- Users / Shares ------------------------------------------------------

  lookupUserByEmail(email, token) {
    const qs = new URLSearchParams({ email });
    return apiGet<dto.UserLookupResponse>(`/users/lookup?${qs.toString()}`, { token });
  },

  createShare(body, token) {
    return apiPost<dto.CreateShareResponse>('/shares', body, { token });
  },

  listIncomingShares(token) {
    return apiGet<dto.IncomingSharesResponse>('/shares/incoming', { token });
  },

  listOutgoingShares(rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiGet<dto.OutgoingSharesResponse>(`/shares/outgoing?${qs.toString()}`, { token });
  },

  deleteShare(rootId, recipientUserId, token) {
    return apiDelete<{ ok: true }>(
      `/shares/${encodeURIComponent(rootId)}/${encodeURIComponent(recipientUserId)}`,
      undefined,
      { token },
    );
  },

  // ---- Tags ----------------------------------------------------------------

  createTag(body, token) {
    return apiPost<dto.CreateTagResponse>('/tags', body, { token });
  },

  renameTag(tagId, body, token) {
    return apiPatch<dto.RenameTagResponse>(`/tags/${encodeURIComponent(tagId)}`, body, { token });
  },

  deleteTag(tagId, rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiDelete<{ ok: true }>(
      `/tags/${encodeURIComponent(tagId)}?${qs.toString()}`,
      undefined,
      { token },
    );
  },

  attachTagsBatch(body, token) {
    return apiPost<dto.AttachTagsBatchResponse>('/photos/tags/attach-batch', body, { token });
  },

  detachTagsBatch(body, token) {
    return apiPost<dto.DetachTagsBatchResponse>('/photos/tags/detach-batch', body, { token });
  },

  listTags(rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiGet<dto.TagsListResponse>(`/tags?${qs.toString()}`, { token });
  },

  getTag(tagId, rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiGet<dto.TagDefinition>(`/tags/${encodeURIComponent(tagId)}?${qs.toString()}`, {
      token,
    });
  },

  listTagPhotos(tagId, rootId, token, cursor) {
    const qs = new URLSearchParams({ rootId });
    if (cursor) qs.set('cursor', cursor);
    return apiGet<dto.TagPhotosResponse>(
      `/tags/${encodeURIComponent(tagId)}/photos?${qs.toString()}`,
      { token },
    );
  },

  listPhotoTags(photoId, rootId, token) {
    const qs = new URLSearchParams({ rootId });
    return apiGet<dto.PhotoTagsResponse>(
      `/photos/${encodeURIComponent(photoId)}/tags?${qs.toString()}`,
      { token },
    );
  },

  // ---- Rating --------------------------------------------------------------

  ratePhotosBatch(body, token) {
    return apiPost<dto.RatePhotosBatchResponse>('/photos/rating-batch', body, { token });
  },

  // ---- Rotation ------------------------------------------------------------

  rotateInit(rootId, body, token) {
    return apiPost<dto.RotateInitResponse>(
      `/albums/${encodeURIComponent(rootId)}/rotation/init`,
      body,
      { token },
    );
  },

  rotationPending(rootId, token) {
    return apiGet<dto.RotationPendingResponse>(
      `/albums/${encodeURIComponent(rootId)}/rotation/pending`,
      { token },
    );
  },

  rotatePhotosBatch(rootId, body, token) {
    return apiPost<dto.RotatePhotosBatchResponse>(
      `/albums/${encodeURIComponent(rootId)}/rotation/photos/batch`,
      body,
      { token },
    );
  },

  rotatePhotosComplete(rootId, body, token) {
    return apiPost<{ ok: true }>(
      `/albums/${encodeURIComponent(rootId)}/rotation/photos/complete`,
      body,
      { token },
    );
  },

  rotateCommit(rootId, body, token) {
    return apiPost<dto.RotateCommitResponse>(
      `/albums/${encodeURIComponent(rootId)}/rotation/commit`,
      body,
      { token },
    );
  },

  rotateTagsCommit(rootId, body, token) {
    return apiPost<dto.RotateTagsCommitResponse>(
      `/albums/${encodeURIComponent(rootId)}/rotation/tags/commit`,
      body,
      { token },
    );
  },

  // ---- Storage admin (S3 bucket onboarding) --------------------------------

  fetchBucketStatus(token) {
    return apiGet<dto.BucketStatusResponse>('/buckets/me', { token });
  },

  fetchOnboardingTemplate(token) {
    return apiGet<dto.OnboardingTemplateResponse>('/buckets/cloudformation-template', { token });
  },

  connectBucket(body, token) {
    return apiPost<dto.BucketStatusResponse>('/buckets/connect', body, { token });
  },

  wipeBucket(token) {
    return apiDelete<dto.BucketWipeResponse>('/buckets', { confirm: 'WIPE' }, { token });
  },
};
